import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, CheckCircle2, Loader2, PenLine, Save, ScanBarcode, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TourneeSelect } from "@/components/map/ZoneDialog";
import { api, errMsg } from "@/lib/api";
import { ChauffeurPicker } from "./ChauffeurPicker";
import { DefautsPanel, EtatScanButton, defautsPayload } from "./DefautsEditor";
import { SignaturePad } from "./SignaturePad";
import { CodeBarre } from "./CodeBarre";
import { defautTexte, nomChauffeur } from "./common";

const ATTRIBUABLES = ["disponible", "preattribuee"];

/**
 * Attribution d'une scan : n° de scan, tournée (pré-remplie depuis la config),
 * chauffeur, état (éléments défectueux), puis Enregistrer (pré-attribution)
 * ou Signature (synthèse -> signature du chauffeur -> attribuée).
 */
export function AttributionDialog({ open, initialScan, scans, tournees, chauffeurs, categories, onClose, onDone }) {
  const [step, setStep] = useState("form"); // form | etat | synthese | signature
  const [scanId, setScanId] = useState(null);
  const [tournee, setTournee] = useState(null);
  const [chauffeurId, setChauffeurId] = useState(null);
  const [appareilId, setAppareilId] = useState(null);   // matériel remis (si plusieurs appareils)
  const [defauts, setDefauts] = useState([]);
  const [etatDraft, setEtatDraft] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sigEmpty, setSigEmpty] = useState(true);
  const sigRef = useRef(null);
  // Config scan › « Remise contre signature » : sinon, la remise est simplement validée.
  const [contreSignature, setContreSignature] = useState(false);
  const [codesBarres, setCodesBarres] = useState({});   // tournée -> code-barres (Config scan)
  useEffect(() => {
    if (!open) return;
    api.get("/scans/reglages").then(({ data }) => setContreSignature(!!data.contre_signature)).catch(() => {});
    api.get("/scans/codes-barres").then(({ data }) => setCodesBarres(data || {})).catch(() => {});
  }, [open]);

  const disponibles = useMemo(
    () => scans.filter((s) => s.actif !== false && ATTRIBUABLES.includes(s.statut)),
    [scans],
  );
  const scan = scans.find((s) => s.id === scanId);
  const pre = scan?.statut === "preattribuee" ? scan.attribution : null;
  const tByCode = useMemo(() => Object.fromEntries(tournees.map((t) => [t.code, t])), [tournees]);
  const chauffeur = chauffeurs.find((c) => c.id === chauffeurId);
  const actifs = scan?.appareils_actifs || [];
  const materiel = actifs.find((x) => x.id === appareilId) || null;
  const materielManquant = actifs.length > 1 && !materiel;

  const prefill = (s) => {
    if (!s) { setTournee(null); setChauffeurId(null); setDefauts([]); setAppareilId(null); return; }
    const a = s.statut === "preattribuee" ? s.attribution : null;
    setTournee(a?.tournee || s.tournee_defaut || null);
    setChauffeurId(a?.chauffeur?.id || null);
    const actifs = s.appareils_actifs || [];
    setAppareilId(actifs.length === 1 ? actifs[0].id : (actifs.find((x) => x.id === a?.appareil?.id)?.id || null));
    setDefauts(s.defauts || []);
  };

  useEffect(() => {
    if (!open) return;
    setStep("form");
    setBusy(false);
    setSigEmpty(true);
    setScanId(initialScan?.id || null);
    prefill(initialScan || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialScan]);

  const choisirScan = (id) => { setScanId(id); prefill(scans.find((s) => s.id === id)); };

  const envoyer = async (action, signature) => {
    setBusy(true);
    try {
      const { data } = await api.post("/scans/attributions", {
        scan_id: scanId, tournee, chauffeur_id: chauffeurId, defauts: defautsPayload(defauts), action, signature, appareil_id: appareilId,
      });
      toast.success(action === "signer"
        ? `Scan ${scan.numero}${materiel ? ` n° ${materiel.numero_serie}` : ""} remise à ${nomChauffeur(chauffeur)} — ${signature ? "signée" : "validée"} le ${new Date(data.signe_le).toLocaleString("fr-FR")}`
        : `Scan n° ${scan.numero} pré-attribuée${chauffeur ? ` à ${nomChauffeur(chauffeur)}` : ""}`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const annulerPre = async () => {
    if (!pre) return;
    setBusy(true);
    try {
      await api.delete(`/scans/attributions/${pre.id}`);
      toast.success(`Pré-attribution de la scan n° ${scan.numero} annulée`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const titre = {
    form: pre ? `Scan n° ${scan?.numero} — pré-attribuée` : "Attribuer une scan",
    etat: "État de la scan",
    synthese: contreSignature ? "Synthèse avant signature" : "Synthèse de la remise",
    signature: "Signature du chauffeur",
  }[step];

  const Ligne = ({ label, children }) => (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-medium text-right">{children}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[94dvh] overflow-y-auto" data-testid="attribution-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanBarcode size={18} /> {titre}</DialogTitle>
          {step === "form" && <DialogDescription>Enregistrez pour préparer la remise à l'avance ; il n'y aura plus qu'à la valider à l'arrivée du chauffeur.</DialogDescription>}
        </DialogHeader>

        {step === "form" && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>N° de scan</Label>
              {initialScan ? (
                <div className="h-10 px-3 rounded-md border border-input bg-secondary/50 flex items-center font-mono font-semibold" data-testid="attr-scan-fixe">
                  {initialScan.numero}
                </div>
              ) : (
                <Select value={scanId || ""} onValueChange={choisirScan}>
                  <SelectTrigger data-testid="attr-scan"><SelectValue placeholder="Choisir une scan disponible…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {disponibles.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <span className="font-mono font-semibold">{s.numero}</span>
                        <span className="text-muted-foreground ml-2">{s.tournee_defaut ? `T. ${s.tournee_defaut}` : ""}{s.statut === "preattribuee" ? " · pré-attribuée" : ""}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Tournée</Label>
              <TourneeSelect tournees={tournees} value={tournee} onChange={setTournee} allowNone noneLabel="— Aucune —" testId="attr-tournee" />
              {scan?.tournee_defaut && tournee === scan.tournee_defaut && <p className="text-[11px] text-muted-foreground">Tournée par défaut de cette scan (config scan).</p>}
            </div>

            {actifs.length > 1 && (
              <div className="space-y-1.5">
                <Label>Matériel associé *</Label>
                <Select value={appareilId || undefined} onValueChange={setAppareilId}>
                  <SelectTrigger data-testid="attr-materiel"><SelectValue placeholder="Choisir l'appareil remis…" /></SelectTrigger>
                  <SelectContent>
                    {actifs.map((x) => (
                      <SelectItem key={x.id} value={x.id}><span className="font-mono font-semibold">{x.numero_serie}</span></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">Cette scan a {actifs.length} appareils : choisissez celui qui est remis.</p>
              </div>
            )}
            {actifs.length === 1 && <p className="text-xs text-muted-foreground">Matériel : <span className="font-mono font-semibold text-foreground">{actifs[0].numero_serie}</span></p>}

            <div className="space-y-1.5">
              <Label>Chauffeur</Label>
              <ChauffeurPicker chauffeurs={chauffeurs} tournee={tournee} value={chauffeurId} onChange={setChauffeurId} />
            </div>

            <EtatScanButton defauts={defauts} onClick={() => { setEtatDraft(defauts); setStep("etat"); }} />

            <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
              {pre && (
                <Button type="button" variant="ghost" className="text-destructive sm:mr-auto" onClick={annulerPre} disabled={busy}>
                  <Trash2 size={14} /> Annuler la pré-attribution
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => envoyer("enregistrer")} disabled={!scanId || busy} data-testid="attr-enregistrer">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
              </Button>
              <Button type="button" onClick={() => setStep("synthese")} disabled={!scanId || !chauffeurId || materielManquant || busy} data-testid="attr-signature">
                {contreSignature ? <><PenLine size={14} /> Signature</> : <><CheckCircle2 size={14} /> Remettre</>}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "etat" && (
          <div className="space-y-4">
            <DefautsPanel list={etatDraft} setList={setEtatDraft} categories={categories} />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setStep("form")}><ArrowLeft size={14} /> Retour</Button>
              <Button type="button" onClick={() => { setDefauts(etatDraft); setStep("form"); }} data-testid="etat-valider">Valider l'état</Button>
            </DialogFooter>
          </div>
        )}

        {step === "synthese" && (
          <div className="space-y-4" data-testid="attr-synthese">
            <div className="rounded-lg border border-border px-3">
              <Ligne label="Scan"><span className="font-mono">{scan?.numero}{materiel ? ` n° ${materiel.numero_serie}` : ""}</span></Ligne>
              <Ligne label="Tournée">{tournee ? `${tournee}${tByCode[tournee]?.libelle ? ` — ${tByCode[tournee].libelle}` : ""}` : "—"}</Ligne>
              <Ligne label="Chauffeur">{nomChauffeur(chauffeur)}{chauffeur?.telephone ? <span className="block text-xs text-muted-foreground font-mono">{chauffeur.telephone}</span> : null}</Ligne>
              <Ligne label="SST">{scan?.sst || chauffeur?.sst || "—"}</Ligne>
              {materiel && <Ligne label="Matériel"><span className="font-mono">{materiel.numero_serie}</span></Ligne>}
              <Ligne label="État de la scan">
                {defauts.length ? (
                  <span className="block space-y-0.5">{defauts.map((d, i) => <span key={d.id || i} className="block text-red-600 dark:text-red-400">{defautTexte(d)}</span>)}</span>
                ) : "Aucun défaut signalé"}
              </Ligne>
            </div>
            {tournee && codesBarres[tournee] && (
              <div className="rounded-lg border border-border bg-white p-2 text-center" data-testid="synthese-code-barre">
                <p className="text-xs font-semibold text-neutral-700 mb-1">Code-barres de la tournée {tournee} — à scanner par le chauffeur</p>
                <CodeBarre valeur={codesBarres[tournee]} hauteur={80} largeur={2} className="mx-auto" testId="synthese-code-barre-svg" />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {contreSignature
                ? "En signant, le chauffeur reconnaît avoir reçu la scan dans l'état décrit ci-dessus."
                : "La scan est remise au chauffeur dans l'état décrit ci-dessus."}
            </p>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setStep("form")}><ArrowLeft size={14} /> Modifier</Button>
              {contreSignature
                ? <Button type="button" onClick={() => { setSigEmpty(true); setStep("signature"); }} data-testid="attr-signer"><PenLine size={14} /> Signer</Button>
                : <Button type="button" onClick={() => envoyer("signer")} disabled={busy} data-testid="attr-valider-remise">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Valider la remise
                </Button>}
            </DialogFooter>
          </div>
        )}

        {step === "signature" && (
          <div className="space-y-4">
            <p className="text-sm">
              <b>{nomChauffeur(chauffeur)}</b> — scan n° <span className="font-mono font-semibold">{scan?.numero}</span>{tournee ? `, tournée ${tournee}` : ""}
            </p>
            <SignaturePad ref={sigRef} onChange={setSigEmpty} />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setStep("synthese")} disabled={busy}><ArrowLeft size={14} /> Retour</Button>
              <Button
                type="button"
                onClick={() => envoyer("signer", sigRef.current.toDataURL())}
                disabled={sigEmpty || busy}
                data-testid="attr-valider-signature"
              >
                {busy && <Loader2 size={14} className="animate-spin" />} Valider la signature
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
